import type { DocumentStats, ReportStatus } from '../../lib/reports';

/**
 * 문서관리 목록의 상태 탭 — 렌더와 떼어 둔 순수 함수 층.
 *
 * 탭이 "보고서 / TBM"(유형 축)에서 "전체 / 초안 / 결재 대기 / 결재 완료 / 내가 작성"
 * (상태 축)으로 바뀌면서, 탭이 어떤 필터를 소유하고 반대로 지금 필터가 어느 탭인지가
 * 렌더 JSX 안에서 읽히지 않게 됐다. 그 대응 규칙만 여기로 떼어 단위 테스트로 고정한다
 * (`__tests__/documentTabs.test.ts`).
 */

/** 탭 = 목록 범위의 정본. 배열 순서가 곧 탭 순서다. */
export type DocumentTab = 'all' | 'draft' | 'pending' | 'approved' | 'mine';

/**
 * 탭이 소유하는 필터 축 — 상태와 "내가 작성한 것만" 둘뿐이다.
 * 현장·유형·검색은 탭과 직교하는 축이라 탭을 옮겨도 그대로 남는다.
 */
export interface DocumentTabScope {
  status: ReportStatus | '';
  mine: boolean;
}

export const DOCUMENT_TABS: {
  tab: DocumentTab;
  label: string;
  scope: DocumentTabScope;
  /** 배지 수의 출처 — 탭과 통계 카드가 같은 수를 보게 키로 묶어 둔다. */
  countKey: keyof Pick<DocumentStats, 'total' | 'draft' | 'pendingApproval' | 'approved' | 'mine'>;
}[] = [
  { tab: 'all', label: '전체', scope: { status: '', mine: false }, countKey: 'total' },
  // 초안 탭 = **DRAFT + CREATING**(결정 2026-09-06 ❓4 확정, W4d) — §7로 위임 문서가
  // CREATING으로 태어나면서 종전 초안 탭에 보이던 위임 건이 빠지지 않게, 서버가 status
  // **집합**을 받게 됐다(`parseStatusFilter`·stats draft 카운트도 같은 집합). 탭 state·URL은
  // 종전대로 단일 `'DRAFT'`를 유지하고(딥링크 `?status=DRAFT` 불변), 질의 시점에만
  // `expandStatusQueryScope`(아래)로 넓힌다 — 두 상태의 구분은 목록의 "생성 중"/"위임
  // 작성중" 파생 배지가 담당한다.
  { tab: 'draft', label: '초안', scope: { status: 'DRAFT', mine: false }, countKey: 'draft' },
  {
    tab: 'pending',
    label: '결재 대기',
    scope: { status: 'PENDING_APPROVAL', mine: false },
    countKey: 'pendingApproval',
  },
  {
    tab: 'approved',
    label: '결재 완료',
    scope: { status: 'APPROVED', mine: false },
    countKey: 'approved',
  },
  { tab: 'mine', label: '내가 작성', scope: { status: '', mine: true }, countKey: 'mine' },
];

/** 탭을 누르면 세팅될 필터. */
export function getDocumentTabScope(tab: DocumentTab): DocumentTabScope {
  return DOCUMENT_TABS.find((entry) => entry.tab === tab)!.scope;
}

/**
 * 상태 state → 서버 질의 스코프 (W4d, 결정 2026-09-06 ❓4).
 *
 * `'DRAFT'`만 집합으로 넓어진다: 초안 탭(=상태 셀렉트의 '초안/생성됨/진행 중' — 두 축은 같은 state
 * 변수 하나라 이 페이지에서 분리되지 않는다, `resolveActiveDocumentTab` 주석)이
 * DRAFT+CREATING을 한 스코프로 갖는다. "생성 중"만 보고 싶으면 셀렉트의 `CREATING` 단독
 * 옵션이 그대로 있다. 배지 수(`DocumentStats.draft`)도 서버가 같은 집합으로 세므로
 * 카드=필터 규약이 유지된다.
 */
export function expandStatusQueryScope(
  status: ReportStatus | '',
): ReportStatus | ReportStatus[] | undefined {
  if (!status) return undefined;
  if (status === 'DRAFT') return ['DRAFT', 'CREATING'];
  return status;
}

/**
 * 지금 필터가 어느 탭인지 — **탭이 세팅하는 값과 정확히 같을 때만** 그 탭이다.
 *
 * 상태 셀렉트는 탭보다 세밀해서(반려됨·서명 중 등) 탭으로 표현되지 않는 상태를 고를 수
 * 있고, 그때는 어느 탭도 활성이 아니다(null). '전체'가 그 자리를 대신 차지하면 목록에
 * 반려 건만 있는데 탭은 '전체'라고 우기는 화면이 된다. 마찬가지로 '내가 작성' 탭에서
 * 상태를 좁히면 그 탭도 풀린다 — 카드·탭은 목록과 같은 축일 때만 켜진다는 규약
 * (`components/StatTile` 주석)을 탭에도 그대로 적용한 것이다.
 */
export function resolveActiveDocumentTab(scope: DocumentTabScope): DocumentTab | null {
  const matched = DOCUMENT_TABS.find(
    (entry) => entry.scope.status === scope.status && entry.scope.mine === scope.mine,
  );
  return matched?.tab ?? null;
}

/**
 * 탭 배지 수 — 통계를 못 읽었으면 0으로 속이지 말고 null을 준다(배지를 뺀다).
 * 이 수는 검색·유형 필터와 무관한 전체 기준이다(현장 필터만 통계에 반영된다).
 */
export function getDocumentTabCount(
  stats: DocumentStats | null,
  tab: DocumentTab,
): number | null {
  if (!stats) return null;
  const entry = DOCUMENT_TABS.find((item) => item.tab === tab);
  return entry ? stats[entry.countKey] : null;
}
